import React, { useLayoutEffect, useMemo } from 'react';
import { Platform, View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Animated from 'react-native-reanimated';
import Text from 'design-system/Text';
import { rem, a, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { AppView, Spacer, withAnimatedNavigationHeader } from 'design-system';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import links from 'utils/links';
import useStyles from 'hooks/useStyles';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import { selectUser } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import { selectIsTradingAccountAlpaca } from '../reducers/selectors';
import { useStockMeta } from '../hooks';
import Receipt from '../components/Receipt';
import { useFilledOrderCache } from '../hooks/useOrderCache';
import TouchableBounceHighlight from '../components/TouchableBounceHighlight';
import { isWeb } from '../../../constants';
const strings = {
    viewTradeConfirmation: 'View trade confirmation',
    close: 'Close',
};
const hitSlop = { top: 20, bottom: 20, left: 40, right: 40 };
const ReceiptScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const activity = route.params?.activity;
    const order = useFilledOrderCache(activity);
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle, styles.container);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => <NavigationCloseButton inWebHeader onPressIn={navigation.goBack} colorKey="primary"/>,
        });
    }, []);
    const confirmationButton = useMemo(() => {
        // this node is memoized, we don’t need to additionally memoize `onPress` callback
        const url = order?.confirmation?.url;
        if (url) {
            if (Platform.OS === 'ios' && a(false, false, false, true, true)) {
                // only for iPhone X and XSMax-like, because for them we are sure the button will fit
                return (<TouchableBounceHighlight white style={styles.viewTradeConfirmationContainer} onPress={() => links.openLink(url)}>
              <Text Text-16 Primary>
                {strings.viewTradeConfirmation}
              </Text>
            </TouchableBounceHighlight>);
            }
            // For others we are not sure it fits, so we are rendering plain touchable text
            return (<TouchableBounce onPress={() => {
                    if (Platform.OS === 'android') {
                        navigation.navigateDeprecated('Pdf', { url, withTopPadding: true });
                    }
                    else {
                        links.openLink(url);
                    }
                }} hitSlop={hitSlop}>
            <Text Text-16 Primary>
              {strings.viewTradeConfirmation}
            </Text>
          </TouchableBounce>);
        }
        return null;
    }, [styles, order]);
    const user = useAppSelector(selectUser);
    const stock = useStockMeta(activity.assetId);
    const isTradingAccountAlpaca = useAppSelector(selectIsTradingAccountAlpaca);
    const pricing = useMemo(() => (order ? order.filled : undefined), [order]);
    let name = activity.symbol || '';
    if (stock?.name) {
        name = stock.name;
        if (activity.type === 'buy')
            name = `Buy ${name}`;
        else if (activity.type === 'sell')
            name = `Sell ${name}`;
    }
    const titleExtra = activity.isDividendReinvest ? 'Dividend reinvested' : undefined;
    const rows = useMemo(() => {
        if (pricing == null)
            return null;
        const out = {};
        out.completeTime = activity.timestamp;
        out.clientName = user.firstName ? `${user.firstName} ${user.lastName}` : user.lastName;
        // eslint-disable-next-line prefer-destructuring
        out.orderId = activity.id.split('-')[0];
        out.numOfShares = pricing.quantity;
        out.pricePerShare = pricing.price;
        if ('fxConversion' in pricing) {
            out.fxRate = {
                from: { value: 1, currency: pricing.fxConversion.rateFrom },
                to: { value: pricing.fxConversion.rate, currency: pricing.fxConversion.rateTo },
            };
            out.fxRateBase = {
                from: { value: 1, currency: pricing.fxConversion.rateFrom },
                to: { value: pricing.fxConversion.base, currency: pricing.fxConversion.rateTo },
            };
            out.fxFee = pricing.fxConversion.fee;
            out.fxCommission = order?.commission;
        }
        else {
            const fee = { value: 0, currency: 'GBP' };
            if (isTradingAccountAlpaca) {
                out.alpacaCommission = fee;
            }
            else {
                out.tradingCharge = fee;
            }
        }
        if (activity.type === 'sell') {
            out.totalAmount = pricing.amount;
        }
        else {
            out.totalCost = pricing.amount;
        }
        return out;
    }, [activity, isTradingAccountAlpaca, order?.commission, pricing, user.firstName, user.lastName]);
    return (<AppView>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle}>
          <Receipt title={name} titleExtra={titleExtra} logoUrl={stock?.iconUrl} rows={rows}/>
          <Spacer h={16}/>
          <View style={styles.flexCenteredWithPadding}>{confirmationButton}</View>
        </Animated.ScrollView>
        <Button brand floating title={strings.close} onPress={navigation.goBack}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export const styleSet = createStyleSheets(() => ({
    container: {
        paddingHorizontal: rem(16),
    },
    flexCenteredWithPadding: {
        alignItems: 'center',
    },
    viewTradeConfirmationContainer: {
        paddingHorizontal: rem(40),
        flexGrow: 0,
        flexShrink: 1,
        flexBasis: rem(48),
        justifyContent: 'center',
    },
}));
export default ReceiptScreen;
